import { Card } from "@pericles/framework-frontend";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  fetchFoundryV2ArtifactAssurance,
  createFoundryV2ExecutionRun,
  fetchFoundryV2ControlTower,
  fetchFoundryV2DeployCandidate,
  fetchFoundryV2ExecutivePack,
  fetchFoundryV2ExecutionRun,
  fetchFoundryV2Launchability,
  fetchFoundryV2ReleaseBundle,
  fetchFoundryV2RolloutState,
  fetchLatestFoundryV2GovernanceApproval,
  fetchLatestFoundryV2Handoff,
  listFoundryV2ExecutionRuns,
  type FoundryV2ArtifactAssuranceCheck,
  type FoundryV2ArtifactAssuranceRecord,
  type FoundryV2ArtifactAssuranceSection,
  type FoundryV2ControlTowerMetric,
  type FoundryV2ControlTowerSnapshot,
  type FoundryV2ControlTowerStep,
  type FoundryV2DeployCandidateRecord,
  type FoundryV2ExecutivePackRecord,
  type FoundryV2GovernanceApprovalRecord,
  type FoundryV2ExecutionRunRecord,
  type FoundryV2HandoffRecord,
  type FoundryV2LaunchabilityRecord,
  type FoundryV2ReleaseBundleRecord,
  type FoundryV2TenantRolloutStateRecord,
} from "../api/foundryV2Client";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import { useOpenAiSecretStatus } from "../hooks/useOpenAiSecretStatus";
import { FoundryV2AccessGuard } from "./FoundryV2AccessGuard";
import { Button } from "./ui";

type V2Module = {
  id: "foundry-v2" | "jarvis-v2" | "governance-v2";
  title: string;
  route: string;
  description: string;
};

const V2_MODULES: V2Module[] = [
  {
    id: "foundry-v2",
    title: "Foundry V2",
    route: "/foundry-v2",
    description: "Intent, architecture recommendations, and stack decisions.",
  },
  {
    id: "jarvis-v2",
    title: "Jarvis V2",
    route: "/jarvis-v2",
    description: "Autonomous planning and runnable application generation.",
  },
  {
    id: "governance-v2",
    title: "Governance V2",
    route: "/governance-v2",
    description: "Risk controls, approval workflows, and enterprise evidence.",
  },
];

type JarvisV2Provider = "stub" | "openai" | "rules_deterministic";
const ENV_VALUES_KEY = "longwalls.v2.envValues";

const PIPELINE_STEPS: Array<{ title: string; description: string; expectedOutput: string }> = [
  {
    title: "1. Capture intent and constraints",
    description: "Collect business outcomes, compliance constraints, and target operating model.",
    expectedOutput: "Validated discovery contract",
  },
  {
    title: "2. Select architecture with AI recommendations",
    description: "Use guided presets for runtime, data, deployment, and integrations.",
    expectedOutput: "Approved architecture profile",
  },
  {
    title: "3. Provision environment contract",
    description: "Complete one setup form for secrets, providers, and repository options.",
    expectedOutput: "Environment readiness package",
  },
  {
    title: "4. Generate runnable application",
    description: "Jarvis decomposes internally, builds code, runs gates, and prepares deliverables.",
    expectedOutput: "Runnable app + full evidence bundle",
  },
];

function buildEnvStorageKey(projectId: string, handoffId: string): string {
  return `${ENV_VALUES_KEY}:${projectId}:${handoffId}`;
}

function readEnvValues(projectId: string, handoffId: string): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(buildEnvStorageKey(projectId, handoffId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, string>>((acc, [key, value]) => {
      if (typeof value === "string") {
        acc[key] = value;
      }
      return acc;
    }, {});
  } catch {
    return {};
  }
}

function writeEnvValues(projectId: string, handoffId: string, values: Record<string, string>): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(buildEnvStorageKey(projectId, handoffId), JSON.stringify(values));
}

function resolveActiveModule(pathname: string): V2Module["id"] {
  if (pathname.startsWith("/foundry-v2")) return "foundry-v2";
  if (pathname.startsWith("/governance-v2")) return "governance-v2";
  return "jarvis-v2";
}

function formatDuration(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "-";
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function governanceEscalationBadgeClass(escalation: FoundryV2GovernanceApprovalRecord["escalation"]): string {
  if (escalation === "escalated") return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
  if (escalation === "breached") return "rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-orange-700";
  if (escalation === "watch") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700";
}

function controlTowerDecisionBadgeClass(decision: FoundryV2ControlTowerSnapshot["summary"]["decision"]): string {
  if (decision === "GO") return "rounded-full border border-emerald-300 bg-emerald-400/15 px-2 py-0.5 text-emerald-100";
  if (decision === "WATCH") return "rounded-full border border-amber-300 bg-amber-400/15 px-2 py-0.5 text-amber-100";
  return "rounded-full border border-red-300 bg-red-400/15 px-2 py-0.5 text-red-100";
}

function operatingDecisionBadgeClass(decision: FoundryV2ControlTowerSnapshot["summary"]["decision"]): string {
  if (decision === "GO") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (decision === "WATCH") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

function controlTowerMetricBadgeClass(status: FoundryV2ControlTowerMetric["status"]): string {
  if (status === "good") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (status === "watch") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  if (status === "critical") return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
  return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600";
}

function controlTowerStepBadgeClass(status: FoundryV2ControlTowerStep["status"]): string {
  if (status === "complete") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (status === "in_progress") return "rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700";
  if (status === "blocked") return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
  return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600";
}

function rolloutSignalBadgeClass(signal: FoundryV2ExecutivePackRecord["rollout"]["promotionSignal"]): string {
  if (signal === "PROMOTE_DEFAULT" || signal === "PROMOTE_STAGED" || signal === "PROMOTE_PILOT") {
    return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  }
  return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
}

function rolloutPhaseBadgeClass(phase: FoundryV2ExecutivePackRecord["rollout"]["currentMode"]): string {
  if (phase === "default") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (phase === "staged") return "rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700";
  if (phase === "pilot") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700";
}

function tenantRolloutModeBadgeClass(mode: FoundryV2TenantRolloutStateRecord["mode"]): string {
  if (mode === "default") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (mode === "staged") return "rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700";
  if (mode === "pilot") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  if (mode === "legacy-fallback-only") return "rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-orange-700";
  return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700";
}

function releaseStatusBadgeClass(status: FoundryV2ReleaseBundleRecord["summary"]["releaseStatus"]): string {
  if (status === "ready") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (status === "watch") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

function artifactAssuranceStatusBadgeClass(status: FoundryV2ArtifactAssuranceRecord["summary"]["status"]): string {
  if (status === "ready") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (status === "watch") return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

function artifactCheckBadgeClass(status: FoundryV2ArtifactAssuranceCheck["status"]): string {
  if (status === "ready") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

function launchabilityStatusBadgeClass(status: FoundryV2LaunchabilityRecord["contract"]["summary"]["status"]): string {
  if (status === "launchable_from_foundry_green") {
    return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  }
  if (status === "watch") {
    return "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700";
  }
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

function launchLanePhaseBadgeClass(phase: FoundryV2ExecutionRunRecord["launchLane"]["phase"]): string {
  if (phase === "deployed") return "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700";
  if (phase === "deploying" || phase === "building" || phase === "claimed") {
    return "rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-blue-700";
  }
  if (phase === "queued") return "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-700";
  return "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-red-700";
}

export function JarvisV2WorkspaceScreen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const activeModule = resolveActiveModule(location.pathname);
  const [provider, setProvider] = useState<JarvisV2Provider>("stub");
  const [dryRunEnabled, setDryRunEnabled] = useState(true);
  const [latestHandoff, setLatestHandoff] = useState<FoundryV2HandoffRecord | null>(null);
  const [isLoadingHandoff, setIsLoadingHandoff] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [controlTower, setControlTower] = useState<FoundryV2ControlTowerSnapshot | null>(null);
  const [isLoadingControlTower, setIsLoadingControlTower] = useState(false);
  const [controlTowerError, setControlTowerError] = useState<string | null>(null);
  const [launchability, setLaunchability] = useState<FoundryV2LaunchabilityRecord | null>(null);
  const [launchabilityError, setLaunchabilityError] = useState<string | null>(null);
  const [artifactAssurance, setArtifactAssurance] = useState<FoundryV2ArtifactAssuranceRecord | null>(null);
  const [artifactAssuranceError, setArtifactAssuranceError] = useState<string | null>(null);
  const [executivePack, setExecutivePack] = useState<FoundryV2ExecutivePackRecord | null>(null);
  const [executivePackError, setExecutivePackError] = useState<string | null>(null);
  const [rolloutState, setRolloutState] = useState<FoundryV2TenantRolloutStateRecord | null>(null);
  const [releaseBundle, setReleaseBundle] = useState<FoundryV2ReleaseBundleRecord | null>(null);
  const [opsBundleError, setOpsBundleError] = useState<string | null>(null);
  const [runHistory, setRunHistory] = useState<FoundryV2ExecutionRunRecord[]>([]);
  const [deployCandidate, setDeployCandidate] = useState<FoundryV2DeployCandidateRecord | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [isLaunchingRun, setIsLaunchingRun] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [refreshingRunId, setRefreshingRunId] = useState<string | null>(null);
  const [envValues, setEnvValues] = useState<Record<string, string>>({});
  const [envExportMessage, setEnvExportMessage] = useState<string | null>(null);
  const [highRiskStagesApproved, setHighRiskStagesApproved] = useState(false);
  const [approvalApprover, setApprovalApprover] = useState("");
  const [approvalEvidenceRef, setApprovalEvidenceRef] = useState("");
  const [approvalNote, setApprovalNote] = useState("");
  const [latestGovernanceApproval, setLatestGovernanceApproval] =
    useState<FoundryV2GovernanceApprovalRecord | null>(null);

  const {
    status: openAiStatus,
    isLoading: isOpenAiStatusLoading,
    error: openAiStatusError,
    refreshStatus: refreshOpenAiStatus,
  } = useOpenAiSecretStatus(DEFAULT_WORKSPACE_ID);

  const projectId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get("projectId");
    if (fromQuery?.trim()) return fromQuery.trim();
    if (typeof window === "undefined") return "";
    try {
      const raw = window.localStorage.getItem("longwalls.v2.handoffPayload");
      if (!raw) return "";
      const parsed = JSON.parse(raw) as { projectId?: string };
      return parsed.projectId?.trim() ?? "";
    } catch {
      return "";
    }
  }, [location.search]);

  useEffect(() => {
    if (!projectId) {
      setLatestHandoff(null);
      setHandoffError(null);
      setControlTower(null);
      setControlTowerError(null);
      setLaunchability(null);
      setLaunchabilityError(null);
      setArtifactAssurance(null);
      setArtifactAssuranceError(null);
      setExecutivePack(null);
      setExecutivePackError(null);
      setRolloutState(null);
      setReleaseBundle(null);
      setOpsBundleError(null);
      setRunHistory([]);
      setDeployCandidate(null);
      setSelectedRunId(null);
      setRunError(null);
      return;
    }

    setIsLoadingHandoff(true);
    setHandoffError(null);
    void fetchLatestFoundryV2Handoff(projectId)
      .then((handoff) => {
        setLatestHandoff(handoff);
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : "Failed to load latest Foundry V2 handoff";
        setHandoffError(message);
        setLatestHandoff(null);
      })
      .finally(() => {
        setIsLoadingHandoff(false);
      });
  }, [projectId]);

  const refreshWorkspace = async (
    targetRunId?: string,
    fallbackRun?: FoundryV2ExecutionRunRecord | null,
  ): Promise<void> => {
    if (!projectId) {
      setControlTower(null);
      setControlTowerError(null);
      setLaunchability(null);
      setLaunchabilityError(null);
      setArtifactAssurance(null);
      setArtifactAssuranceError(null);
      setExecutivePack(null);
      setExecutivePackError(null);
      setRunHistory([]);
      setDeployCandidate(null);
      setSelectedRunId(null);
      return;
    }

    setRunError(null);
    setControlTowerError(null);
    setLaunchabilityError(null);
    setArtifactAssuranceError(null);
    setExecutivePackError(null);
    setOpsBundleError(null);
    setIsLoadingControlTower(true);

    try {
      const [
        runsResult,
        candidateResult,
        controlTowerResult,
        launchabilityResult,
        artifactAssuranceResult,
        executivePackResult,
        rolloutStateResult,
        releaseBundleResult,
      ] = await Promise.allSettled([
        latestHandoff?.id
          ? listFoundryV2ExecutionRuns(projectId, latestHandoff.id)
          : Promise.resolve([] as FoundryV2ExecutionRunRecord[]),
        latestHandoff?.id
          ? fetchFoundryV2DeployCandidate(projectId, latestHandoff.id)
          : Promise.resolve(null as FoundryV2DeployCandidateRecord | null),
        fetchFoundryV2ControlTower(projectId),
        fetchFoundryV2Launchability(projectId),
        fetchFoundryV2ArtifactAssurance(projectId),
        fetchFoundryV2ExecutivePack(projectId),
        fetchFoundryV2RolloutState(projectId),
        fetchFoundryV2ReleaseBundle(projectId),
      ]);

      if (controlTowerResult.status === "fulfilled") {
        setControlTower(controlTowerResult.value);
      } else {
        setControlTowerError(
          controlTowerResult.reason instanceof Error
            ? controlTowerResult.reason.message
            : "Failed to load Foundry V2 control tower",
        );
      }

      if (launchabilityResult.status === "fulfilled") {
        setLaunchability(launchabilityResult.value);
      } else {
        setLaunchability(null);
        setLaunchabilityError(
          launchabilityResult.reason instanceof Error
            ? launchabilityResult.reason.message
            : "Failed to load Foundry launchability",
        );
      }

      if (artifactAssuranceResult.status === "fulfilled") {
        setArtifactAssurance(artifactAssuranceResult.value);
      } else {
        setArtifactAssurance(null);
        setArtifactAssuranceError(
          artifactAssuranceResult.reason instanceof Error
            ? artifactAssuranceResult.reason.message
            : "Failed to load Foundry V2 artifact assurance",
        );
      }

      if (executivePackResult.status === "fulfilled") {
        setExecutivePack(executivePackResult.value);
      } else {
        setExecutivePack(null);
        setExecutivePackError(
          executivePackResult.reason instanceof Error
            ? executivePackResult.reason.message
            : "Failed to load Foundry V2 executive pack",
        );
      }

      if (rolloutStateResult.status === "fulfilled") {
        setRolloutState(rolloutStateResult.value);
      } else {
        setRolloutState(null);
        setOpsBundleError(
          rolloutStateResult.reason instanceof Error
            ? rolloutStateResult.reason.message
            : "Failed to load Foundry V2 rollout state",
        );
      }

      if (releaseBundleResult.status === "fulfilled") {
        setReleaseBundle(releaseBundleResult.value);
      } else {
        setReleaseBundle(null);
        setOpsBundleError((current) =>
          current ??
          (releaseBundleResult.reason instanceof Error
            ? releaseBundleResult.reason.message
            : "Failed to load Foundry V2 release bundle"),
        );
      }

      if (!latestHandoff?.id) {
        setRunHistory([]);
        setDeployCandidate(null);
        setSelectedRunId(null);
        return;
      }

      let runLoadError =
        runsResult.status === "rejected"
          ? runsResult.reason instanceof Error
            ? runsResult.reason.message
            : "Failed to load Jarvis V2 runs"
          : candidateResult.status === "rejected"
            ? candidateResult.reason instanceof Error
              ? candidateResult.reason.message
              : "Failed to load deploy candidate"
            : null;

      let runs = runsResult.status === "fulfilled" ? runsResult.value : [];
      if (targetRunId && !runs.some((run) => run.id === targetRunId)) {
        try {
          const latestRun = await fetchFoundryV2ExecutionRun(projectId, latestHandoff.id, targetRunId);
          if (latestRun) {
            runs = [latestRun, ...runs.filter((run) => run.id !== latestRun.id)];
          }
        } catch (error) {
          runLoadError =
            runLoadError ??
            (error instanceof Error ? error.message : "Failed to refresh the latest Jarvis V2 run");
        }
      }

      if (fallbackRun && (!targetRunId || !runs.some((run) => run.id === targetRunId))) {
        runs = [fallbackRun, ...runs.filter((run) => run.id !== fallbackRun.id)];
      }

      if (!runs.length && runsResult.status !== "fulfilled") {
        setRunHistory([]);
        setDeployCandidate(null);
        setSelectedRunId(null);
        if (runLoadError) setRunError(runLoadError);
        return;
      }

      const candidate = candidateResult.status === "fulfilled" ? candidateResult.value : null;
      setRunHistory(runs);
      setDeployCandidate(candidate);
      setSelectedRunId((current) => {
        if (targetRunId && runs.some((run) => run.id === targetRunId)) return targetRunId;
        return current && runs.some((run) => run.id === current) ? current : (runs[0]?.id ?? null);
      });
      if (runLoadError) {
        setRunError(runLoadError);
      }
    } finally {
      setIsLoadingControlTower(false);
    }
  };

  useEffect(() => {
    void refreshWorkspace();
  }, [projectId, latestHandoff?.id]);

  useEffect(() => {
    if (!projectId || !latestHandoff?.id) {
      setEnvValues({});
      setEnvExportMessage(null);
      return;
    }
    setEnvValues(readEnvValues(projectId, latestHandoff.id));
    setEnvExportMessage(null);
  }, [projectId, latestHandoff?.id]);

  useEffect(() => {
    let cancelled = false;

    if (!projectId || !latestHandoff?.id) {
      setLatestGovernanceApproval(null);
      setHighRiskStagesApproved(false);
      setApprovalApprover("");
      setApprovalEvidenceRef("");
      setApprovalNote("");
      return () => {
        cancelled = true;
      };
    }

    void fetchLatestFoundryV2GovernanceApproval(projectId, latestHandoff.id)
      .then((approval) => {
        if (cancelled) return;
        setLatestGovernanceApproval(approval);
        if (approval?.decision === "approved") {
          setHighRiskStagesApproved(true);
          setApprovalApprover(approval.approver ?? "");
          setApprovalEvidenceRef(approval.evidenceRef ?? "");
          setApprovalNote(approval.note ?? "");
        } else {
          setHighRiskStagesApproved(false);
          setApprovalApprover("");
          setApprovalEvidenceRef("");
          setApprovalNote("");
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLatestGovernanceApproval(null);
        setHighRiskStagesApproved(false);
        setApprovalApprover("");
        setApprovalEvidenceRef("");
        setApprovalNote("");
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, latestHandoff?.id]);

  const selectedRun = useMemo(
    () => runHistory.find((run) => run.id === selectedRunId) ?? null,
    [runHistory, selectedRunId],
  );

  const requiredEnvKeys = latestHandoff?.input.environment.envKeys ?? [];
  const missingEnvKeys = requiredEnvKeys.filter((key) => !envValues[key]?.trim());
  const isEnvContractReady = requiredEnvKeys.length === 0 || missingEnvKeys.length === 0;
  const launchabilityHardBlockers = (launchability?.contract.blockers ?? []).filter(
    (blocker) => blocker.severity === "blocking" && blocker.source !== "environment",
  );
  const isLaunchabilityHardBlocked = launchabilityHardBlockers.length > 0;

  const launchJarvisV2Run = async (): Promise<void> => {
    if (!latestHandoff) {
      setRunError("No Foundry V2 handoff available.");
      return;
    }
    if (latestHandoff.status !== "READY_FOR_JARVIS") {
      setRunError("Handoff is not READY_FOR_JARVIS. Resolve blockers in Foundry V2 first.");
      return;
    }
    if (isLaunchabilityHardBlocked) {
      setRunError(launchability?.contract.summary.headline ?? "Launch lane is blocked by Foundry or runtime blockers.");
      return;
    }
    if (provider === "openai" && !openAiStatus?.configured) {
      setRunError("OpenAI provider selected but no workspace secret is configured.");
      return;
    }
    if (!isEnvContractReady) {
      setRunError(`Environment contract incomplete. Missing keys: ${missingEnvKeys.join(", ")}`);
      return;
    }
    if (highRiskStagesApproved && (!approvalApprover.trim() || !approvalEvidenceRef.trim())) {
      setRunError("Approval evidence incomplete. Fill approver and evidence reference.");
      return;
    }

    setRunError(null);
    setIsLaunchingRun(true);

    try {
      const run = await createFoundryV2ExecutionRun(latestHandoff.projectId, latestHandoff.id, {
        provider,
        environment: "sandbox",
        dryRun: dryRunEnabled,
        envValues,
        approval: {
          highRiskStagesApproved,
          approver: approvalApprover.trim() || undefined,
          evidenceRef: approvalEvidenceRef.trim() || undefined,
          note: approvalNote.trim() || undefined,
        },
      });
      setRunHistory((current) => [run, ...current.filter((entry) => entry.id !== run.id)]);
      setSelectedRunId(run.id);
      await refreshWorkspace(run.id, run);
      if (run.error) {
        setRunError(run.error);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to generate runnable app";
      setRunError(message);
    } finally {
      setIsLaunchingRun(false);
    }
  };

  const updateEnvValue = (key: string, value: string): void => {
    if (!projectId || !latestHandoff?.id) return;
    const next = { ...envValues, [key]: value };
    setEnvValues(next);
    writeEnvValues(projectId, latestHandoff.id, next);
    setEnvExportMessage(null);
  };

  const exportEnvTemplate = async (): Promise<void> => {
    if (!requiredEnvKeys.length) {
      setEnvExportMessage("No environment key is required for this handoff.");
      return;
    }

    const content = requiredEnvKeys.map((key) => `${key}=${envValues[key] ?? ""}`).join("\n");
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(content);
        setEnvExportMessage("Copied .env template to clipboard.");
      } else {
        setEnvExportMessage("Clipboard not available. Copy values manually from the fields.");
      }
    } catch {
      setEnvExportMessage("Clipboard write failed. Copy values manually from the fields.");
    }
  };

  const refreshRunArtifacts = async (runId: string): Promise<void> => {
    if (!projectId || !latestHandoff?.id) return;

    setRefreshingRunId(runId);
    setRunError(null);
    try {
      const run = await fetchFoundryV2ExecutionRun(projectId, latestHandoff.id, runId);
      if (!run) {
        setRunError("Run not found anymore.");
      }
      await refreshWorkspace(runId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to refresh run artifacts";
      setRunError(message);
    } finally {
      setRefreshingRunId(null);
    }
  };

  const isOpenAiConfigured = Boolean(openAiStatus?.configured);

  const withProjectQuery = (route: string): string =>
    projectId ? `${route}?projectId=${encodeURIComponent(projectId)}` : route;

  const controlTowerReasons = controlTower?.summary.reasons ?? [];
  const controlTowerNextActions = controlTower?.summary.nextActions ?? [];
  const executivePackMetrics = executivePack?.delivery.keyMetrics.slice(0, 4) ?? [];
  const artifactSections: FoundryV2ArtifactAssuranceSection[] = [
    artifactAssurance?.latestHandoff,
    artifactAssurance?.latestRun,
    artifactAssurance?.deployCandidate,
  ].filter((section): section is FoundryV2ArtifactAssuranceSection => Boolean(section));

  return (
    <FoundryV2AccessGuard projectId={projectId} target="jarvis-v2">
      <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Jarvis V2</h1>
          <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
            Beta parallel module
          </span>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          This module runs in parallel with legacy LongWalls. Legacy flows remain available until final cutover.
        </p>
      </div>

      <Card className="overflow-hidden border border-slate-200" bodyClassName="space-y-4">
        <div className="rounded-xl bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-4 text-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-blue-200">
                Operational scorecard
              </div>
              {!projectId ? (
                <div className="max-w-2xl text-sm text-blue-50/90">
                  Select a project and create a Foundry V2 handoff to compute Jarvis V2 delivery confidence.
                </div>
              ) : isLoadingControlTower && !controlTower ? (
                <div className="text-sm text-blue-50/90">Computing Foundry V2 control tower...</div>
              ) : controlTower ? (
                <>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={controlTowerDecisionBadgeClass(controlTower.summary.decision)}>
                      {controlTower.summary.decision}
                    </span>
                    <span className="text-sm text-blue-100/90">
                      Generated {new Date(controlTower.generatedAt).toLocaleString()}
                    </span>
                  </div>
                  <h2 className="max-w-3xl text-2xl font-semibold tracking-tight text-white">
                    {controlTower.summary.headline}
                  </h2>
                </>
              ) : controlTowerError ? (
                <div className="max-w-2xl text-sm text-red-100">{controlTowerError}</div>
              ) : (
                <div className="max-w-2xl text-sm text-blue-50/90">
                  Jarvis V2 control tower is waiting for the first handoff and run signal.
                </div>
              )}
            </div>

            {controlTower ? (
              <div className="min-w-[180px] rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur-sm">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-100/80">
                  Delivery confidence score
                </div>
                <div className="mt-2 text-5xl font-bold leading-none text-white">{controlTower.summary.score}</div>
                <div className="mt-2 text-xs text-blue-100/80">
                  {controlTower.counts.goRuns} GO run(s), {controlTower.counts.pendingApprovals} pending approval(s)
                </div>
              </div>
            ) : null}
          </div>

          {controlTower ? (
            <div className="mt-4 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-blue-100/70">Handoffs</div>
                <div className="mt-1 text-lg font-semibold text-white">{controlTower.counts.handoffs}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-blue-100/70">Tracked runs</div>
                <div className="mt-1 text-lg font-semibold text-white">{controlTower.counts.runs}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-blue-100/70">Completed runs</div>
                <div className="mt-1 text-lg font-semibold text-white">{controlTower.counts.completedRuns}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                <div className="text-blue-100/70">High-risk open</div>
                <div className="mt-1 text-lg font-semibold text-white">{controlTower.counts.highRiskOpen}</div>
              </div>
            </div>
          ) : null}
        </div>

        {controlTowerError && controlTower ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Control tower refresh warning: {controlTowerError}
          </div>
        ) : null}

        {controlTower ? (
          <div className="grid gap-4 xl:grid-cols-[1.35fr_1fr]">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {controlTower.metrics.map((metric) => (
                  <div key={metric.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">{metric.label}</div>
                      <span className={controlTowerMetricBadgeClass(metric.status)}>{metric.status}</span>
                    </div>
                    <div className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{metric.displayValue}</div>
                    <div className="mt-2 text-xs leading-5 text-slate-600">{metric.detail}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 text-sm font-semibold text-slate-900">Execution pipeline status</div>
                <div className="grid gap-3 md:grid-cols-2">
                  {controlTower.steps.map((step) => (
                    <div key={step.id} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-semibold text-slate-900">{step.label}</div>
                        <span className={controlTowerStepBadgeClass(step.status)}>{step.status}</span>
                      </div>
                      <div className="mt-2 text-xs leading-5 text-slate-600">{step.detail}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Current reasons</div>
                <div className="mt-3 space-y-2">
                  {controlTowerReasons.length ? (
                    controlTowerReasons.map((reason) => (
                      <div key={reason} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                        {reason}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500">No blocking reason currently recorded.</div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Next actions</div>
                <div className="mt-3 space-y-2">
                  {controlTowerNextActions.length ? (
                    controlTowerNextActions.map((action) => (
                      <div key={action} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                        {action}
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500">
                      No immediate action required. Keep the current operating baseline.
                    </div>
                  )}
                </div>
              </div>

              {controlTower.deployCandidate ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
                  <div className="text-sm font-semibold text-emerald-900">Best deploy candidate</div>
                  <div className="mt-2 text-xs text-emerald-900">
                    Run {controlTower.deployCandidate.run.id} is deploy-ready
                    {controlTower.deployCandidate.promotedByGovernance ? " after Governance V2 promotion" : ""}.
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={controlTower.deployCandidate.run.artifactUrls.deployHandoff}
                    >
                      deploy_handoff.json
                    </a>
                    <a
                      className="text-xs font-medium text-blue-700 underline"
                      href={controlTower.deployCandidate.run.artifactUrls.deliveryResult}
                    >
                      delivery_result.json
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Card>

      <Card className="border border-slate-200" bodyClassName="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-slate-900">Executive briefing</div>
            <p className="mt-1 text-sm text-slate-600">
              Board-ready synthesis of business intent, architecture posture, delivery confidence, and next moves.
            </p>
          </div>
          {executivePack ? (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className={operatingDecisionBadgeClass(executivePack.summary.decision)}>
                {executivePack.summary.decision}
              </span>
              <span className={rolloutSignalBadgeClass(executivePack.rollout.promotionSignal)}>
                {executivePack.rollout.promotionSignal}
              </span>
              <a className="text-blue-700 underline" href={executivePack.artifacts.executivePackJson}>
                executive_pack.json
              </a>
              <a className="text-blue-700 underline" href={executivePack.artifacts.executivePackMarkdown}>
                executive_pack.md
              </a>
            </div>
          ) : null}
        </div>

        {!projectId ? (
          <div className="text-sm text-slate-500">Select a project to compute the Executive Pack.</div>
        ) : isLoadingControlTower && !executivePack ? (
          <div className="text-sm text-slate-600">Synthesizing executive pack...</div>
        ) : executivePack ? (
          <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 p-5 text-white">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={controlTowerDecisionBadgeClass(executivePack.summary.decision)}>
                    {executivePack.summary.decision}
                  </span>
                  <span className="text-xs text-blue-100/80">
                    Generated {new Date(executivePack.generatedAt).toLocaleString()}
                  </span>
                </div>
                <div className="mt-3 text-2xl font-semibold tracking-tight">{executivePack.summary.headline}</div>
                <p className="mt-3 max-w-4xl text-sm leading-6 text-blue-50/90">{executivePack.summary.boardNarrative}</p>
                <div className="mt-4 rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm text-blue-50/90">
                  {executivePack.summary.operatingModel}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900">Business posture</div>
                  <div className="mt-3 space-y-2 text-sm text-slate-700">
                    <div><span className="font-medium text-slate-900">App:</span> {executivePack.businessContext.appName ?? "-"}</div>
                    <div><span className="font-medium text-slate-900">Type:</span> {executivePack.businessContext.appType ?? "-"}</div>
                    <div><span className="font-medium text-slate-900">Goal:</span> {executivePack.businessContext.businessGoal ?? "-"}</div>
                    <div><span className="font-medium text-slate-900">Score:</span> {executivePack.summary.score}/100</div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900">Architecture snapshot</div>
                  <div className="mt-3 grid gap-2 text-sm text-slate-700">
                    <div>{executivePack.architecture.runtime ?? "-"} / {executivePack.architecture.frontend ?? "-"}</div>
                    <div>{executivePack.architecture.backend ?? "-"} + {executivePack.architecture.database ?? "-"}</div>
                    <div>{executivePack.architecture.deployment ?? "-"} with {executivePack.architecture.auth ?? "-"}</div>
                    <div className="text-xs text-slate-500">
                      {executivePack.architecture.rationale ?? "Architecture rationale is not available yet."}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-900">MVP scope</div>
                  <div className="mt-3 space-y-2">
                    {executivePack.businessContext.mvpScope.length ? (
                      executivePack.businessContext.mvpScope.map((item) => (
                        <div key={item} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                          {item}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-slate-500">No MVP scope items captured yet.</div>
                    )}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-900">Target KPIs</div>
                  <div className="mt-3 space-y-2">
                    {executivePack.businessContext.kpis.length ? (
                      executivePack.businessContext.kpis.map((item) => (
                        <div key={item} className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                          {item}
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-slate-500">No KPI target captured yet.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-slate-900">Rollout command center</div>
                  <span className={rolloutSignalBadgeClass(executivePack.rollout.promotionSignal)}>
                    {executivePack.rollout.promotionSignal}
                  </span>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Current mode</div>
                    <div className="mt-2">
                      <span className={rolloutPhaseBadgeClass(executivePack.rollout.currentMode)}>
                        {executivePack.rollout.currentMode}
                      </span>
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Next promotion target</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {executivePack.rollout.nextPromotionPhase ?? "already default"}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Stable review windows</div>
                    <div className="mt-2 text-lg font-semibold text-slate-900">
                      {executivePack.rollout.stableReviewWindows}/{executivePack.rollout.requiredStableReviewWindows}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">Latest review window</div>
                    <div className="mt-2 text-sm font-semibold text-slate-900">
                      {executivePack.rollout.latestReviewWindowLabel ?? "-"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-900">
                  {executivePack.rollout.promotionNarrative}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className={executivePack.rollout.eligibleForDefaultPromotion ? "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700" : "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-700"}>
                    {executivePack.rollout.eligibleForDefaultPromotion ? "Default eligible" : "Default not yet"}
                  </span>
                  <a className="text-blue-700 underline" href={executivePack.artifacts.cutoverDashboardJson}>
                    cutover_dashboard.json
                  </a>
                  <a className="text-blue-700 underline" href={executivePack.artifacts.cutoverDashboardMarkdown}>
                    cutover_dashboard.md
                  </a>
                </div>
              </div>

              {rolloutState || releaseBundle ? (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900">Tenant release status</div>
                    {releaseBundle ? (
                      <span className={releaseStatusBadgeClass(releaseBundle.summary.releaseStatus)}>
                        {releaseBundle.summary.releaseStatus.toUpperCase()}
                      </span>
                    ) : null}
                  </div>
                  {opsBundleError ? (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      {opsBundleError}
                    </div>
                  ) : null}
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">Active rollout mode</div>
                      <div className="mt-2">
                        <span className={tenantRolloutModeBadgeClass(rolloutState?.mode ?? "disabled")}>
                          {rolloutState?.mode ?? "disabled"}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-slate-600">
                        Active path: {rolloutState?.activePath ?? "-"} | source: {rolloutState?.source ?? "-"}
                      </div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">Release bundle</div>
                      <div className="mt-2 text-sm font-semibold text-slate-900">
                        {releaseBundle?.summary.decision ?? "not generated"}
                      </div>
                      <div className="mt-2 text-xs text-slate-600">
                        {releaseBundle?.summary.headline ?? "Compute the release bundle from Governance V2."}
                      </div>
                    </div>
                  </div>
                  {releaseBundle ? (
                    <>
                      <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-5 text-blue-900">
                        {releaseBundle.summary.rationale}
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <a className="text-blue-700 underline" href={releaseBundle.artifacts.releaseBundleJson}>
                          release_bundle.json
                        </a>
                        <a className="text-blue-700 underline" href={releaseBundle.artifacts.releaseBundleMarkdown}>
                          release_bundle.md
                        </a>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Delivery pulse</div>
                <div className="mt-3 grid gap-2 text-xs text-slate-700">
                  <div>Latest handoff: {executivePack.delivery.latestHandoffId ?? "-"}</div>
                  <div>Latest run: {executivePack.delivery.latestRunId ?? "-"}</div>
                  <div>Run status: {executivePack.delivery.latestRunStatus ?? "-"}</div>
                  <div>Deploy candidate: {executivePack.delivery.deployCandidateRunId ?? "-"}</div>
                  <div>Pending approvals: {executivePack.delivery.pendingApprovals}</div>
                  <div>High-risk open: {executivePack.delivery.highRiskOpen}</div>
                </div>
                <div className="mt-4 space-y-2">
                  {executivePackMetrics.map((metric) => (
                    <div key={metric.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-medium text-slate-800">{metric.label}</span>
                        <span className={controlTowerMetricBadgeClass(metric.status)}>{metric.displayValue}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">{metric.detail}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Top risks</div>
                <div className="mt-3 space-y-2">
                  {executivePack.risks.length ? (
                    executivePack.risks.map((risk) => (
                      <div key={risk.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-xs font-medium text-slate-900">{risk.title}</span>
                          <span className={risk.level === "critical" ? "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700" : "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700"}>
                            {risk.level}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-slate-600">{risk.mitigation}</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-xs text-slate-500">No explicit risk currently recorded.</div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-sm font-semibold text-slate-900">Pack artifacts</div>
                <div className="mt-3 grid gap-2 text-xs text-blue-700">
                  <a href={executivePack.artifacts.executivePackJson}>executive_pack.json</a>
                  <a href={executivePack.artifacts.executivePackMarkdown}>executive_pack.md</a>
                  {executivePack.artifacts.contract ? <a href={executivePack.artifacts.contract}>foundry_contract_v1.json</a> : null}
                  {executivePack.artifacts.specDraft ? <a href={executivePack.artifacts.specDraft}>technical_spec_draft_v1.json</a> : null}
                  {executivePack.artifacts.handoffPackage ? <a href={executivePack.artifacts.handoffPackage}>handoff_package.json</a> : null}
                  {executivePack.artifacts.deployHandoff ? <a href={executivePack.artifacts.deployHandoff}>deploy_handoff.json</a> : null}
                  {executivePack.artifacts.deliveryResult ? <a href={executivePack.artifacts.deliveryResult}>delivery_result.json</a> : null}
                  <a href={executivePack.artifacts.cutoverDashboardJson}>cutover_dashboard.json</a>
                  <a href={executivePack.artifacts.cutoverDashboardMarkdown}>cutover_dashboard.md</a>
                </div>
              </div>
            </div>
          </div>
        ) : executivePackError ? (
          <div className="text-sm text-red-700">{executivePackError}</div>
        ) : (
          <div className="text-sm text-slate-500">
            Executive pack will appear once the project has enough Foundry/Jarvis V2 signal.
          </div>
        )}
      </Card>

      <Card className="border border-slate-200" bodyClassName="space-y-2">
        <div className="text-base font-semibold text-slate-900">Foundry V2 handoff</div>
        {!projectId ? (
          <div className="text-sm text-amber-700">
            No project selected. Open `/foundry-v2` and lock a handoff first.
          </div>
        ) : isLoadingHandoff ? (
          <div className="text-sm text-slate-600">Loading latest handoff...</div>
        ) : handoffError ? (
          <div className="text-sm text-red-700">{handoffError}</div>
        ) : latestHandoff ? (
          <div className="space-y-3">
            <div className="grid gap-2 text-sm text-slate-700 md:grid-cols-2">
              <div>Project ID: {latestHandoff.projectId}</div>
              <div>Handoff ID: {latestHandoff.id}</div>
              <div>App: {latestHandoff.input.appName}</div>
              <div>Type: {latestHandoff.input.appType}</div>
              <div>Status: {latestHandoff.status}</div>
              <div>Brief status: {latestHandoff.projectBriefStatus}</div>
              <div>Readiness: {latestHandoff.readiness.decision.toUpperCase()}</div>
              <div>Readiness score: {(latestHandoff.readiness.score * 100).toFixed(0)}%</div>
              <div>Spec sections: {latestHandoff.specSectionCount}</div>
              <div>Planner stages: {latestHandoff.planner?.stageCount ?? 0}</div>
              <div>
                Planner budget: {latestHandoff.planner?.budget?.maxIterations ?? 0} iterations /{" "}
                {latestHandoff.planner?.budget?.maxMinutes ?? 0} min
              </div>
              <div>Generated at: {new Date(latestHandoff.createdAt).toLocaleString()}</div>
            </div>
            <div className="grid gap-1 text-xs text-blue-700">
              <a href={latestHandoff.artifactUrls.handoffPackage}>handoff_package.json</a>
              <a href={latestHandoff.artifactUrls.executionPlan}>execution_plan.json</a>
              <a href={latestHandoff.artifactUrls.executionContract}>execution_contract.json</a>
              <a href={latestHandoff.artifactUrls.projectBrief}>project_brief.json</a>
              <a href={latestHandoff.artifactUrls.projectSpecs}>project_specs.json</a>
              <a href={latestHandoff.artifactUrls.specMvp}>spec_mvp.json</a>
              <a href={latestHandoff.artifactUrls.discoveryContext}>discovery_context.json</a>
              <a href={latestHandoff.artifactUrls.discoveryReadiness}>discovery_readiness.json</a>
            </div>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Artifact assurance</div>
                  <p className="text-xs text-slate-600">
                    Validate that every locked handoff, runtime, and deploy bundle is physically present before rollout.
                  </p>
                </div>
                {artifactAssurance ? (
                  <span className={artifactAssuranceStatusBadgeClass(artifactAssurance.summary.status)}>
                    {artifactAssurance.summary.status.toUpperCase()}
                  </span>
                ) : null}
              </div>

              {!projectId ? (
                <div className="text-xs text-slate-500">Select a project to verify artifact integrity.</div>
              ) : isLoadingControlTower && !artifactAssurance ? (
                <div className="text-xs text-slate-600">Verifying stored handoff and runtime bundles...</div>
              ) : artifactAssuranceError ? (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {artifactAssuranceError}
                </div>
              ) : artifactAssurance ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                    <div className="font-semibold text-slate-900">{artifactAssurance.summary.headline}</div>
                    <div className="mt-1">
                      Available artifacts: {artifactAssurance.summary.availableCount} | Missing artifacts:{" "}
                      {artifactAssurance.summary.missingCount}
                    </div>
                  </div>

                  {artifactAssurance.summary.reasons.length ? (
                    <div className="space-y-2">
                      {artifactAssurance.summary.reasons.map((reason) => (
                        <div
                          key={reason}
                          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                        >
                          {reason}
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="grid gap-3 xl:grid-cols-3">
                    {artifactSections.map((section) => (
                      <div key={`${section.label}-${section.id ?? "none"}`} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">{section.label}</div>
                            <div className="text-[11px] text-slate-500">{section.subject ?? "No subject recorded"}</div>
                          </div>
                          <span className={artifactAssuranceStatusBadgeClass(section.status)}>
                            {section.status.toUpperCase()}
                          </span>
                        </div>
                        <div className="mt-2 text-[11px] text-slate-600">
                          {section.availableCount} ready / {section.missingCount} missing
                        </div>
                        <div className="mt-3 space-y-2">
                          {section.checks.map((check) => (
                            <div key={check.id} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                              <div className="flex items-start justify-between gap-2 text-[11px]">
                                <div className="font-semibold text-slate-900">{check.label}</div>
                                <span className={artifactCheckBadgeClass(check.status)}>{check.status.toUpperCase()}</span>
                              </div>
                              <div className="mt-1 text-[11px] text-slate-600">{check.detail}</div>
                              {check.url ? (
                                <a className="mt-2 inline-block text-[11px] font-medium text-blue-700 underline" href={check.url}>
                                  {check.fileName}
                                </a>
                              ) : null}
                            </div>
                          ))}
                        </div>
                        {section.nextActions.length ? (
                          <div className="mt-3 space-y-1 text-[11px] text-slate-600">
                            {section.nextActions.map((action) => (
                              <div key={action}>{action}</div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {artifactAssurance.summary.nextActions.map((action) => (
                      <div key={action} className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                        {action}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">Artifact assurance will appear once the first handoff is locked.</div>
              )}
            </div>
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Jarvis V2 execution launchpad</div>
                  <p className="text-xs text-slate-600">
                    One click launch from handoff. Jarvis V2 drives generation, smoke gates, and archive packaging.
                  </p>
                </div>
              </div>

              <div className="grid gap-3 text-xs text-slate-700 md:grid-cols-2">
                <label className="space-y-1">
                  <span className="font-semibold text-slate-800">Provider</span>
                  <select
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={provider}
                    onChange={(event) => setProvider(event.target.value as JarvisV2Provider)}
                  >
                    <option value="stub">stub (deterministic)</option>
                    <option value="rules_deterministic">rules_deterministic</option>
                    <option value="openai">openai</option>
                  </select>
                </label>
                <div className="space-y-1">
                  <span className="font-semibold text-slate-800">Environment</span>
                  <div className="rounded border border-slate-200 bg-white px-2 py-1">sandbox</div>
                </div>
                <div className="space-y-1">
                  <span className="font-semibold text-slate-800">Execution mode</span>
                  <label className="flex items-center gap-2 rounded border border-slate-200 bg-white px-2 py-1">
                    <input
                      type="checkbox"
                      checked={dryRunEnabled}
                      onChange={(event) => setDryRunEnabled(event.target.checked)}
                    />
                    Dry-run mode
                  </label>
                </div>
                <div className="space-y-1">
                  <span className="font-semibold text-slate-800">OpenAI workspace secret</span>
                  <div className="flex items-center justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1">
                    <span className={isOpenAiConfigured ? "text-emerald-700" : "text-amber-700"}>
                      {isOpenAiConfigured
                        ? `Configured (${openAiStatus?.previewMasked ?? "masked"})`
                        : "Not configured"}
                    </span>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void refreshOpenAiStatus()}
                      disabled={isOpenAiStatusLoading}
                    >
                      Refresh
                    </Button>
                  </div>
                </div>
              </div>

              {openAiStatusError ? <div className="text-xs text-red-700">{openAiStatusError}</div> : null}

              <div className="space-y-2 rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800">Launchability contract</div>
                  {launchability ? (
                    <span className={launchabilityStatusBadgeClass(launchability.contract.summary.status)}>
                      {launchability.contract.summary.status}
                    </span>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-600">
                      unavailable
                    </span>
                  )}
                </div>
                {launchability ? (
                  <>
                    <div>{launchability.contract.summary.headline}</div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div>Foundry green: {launchability.contract.summary.foundryGreen ? "yes" : "no"}</div>
                      <div>Execution mode: {launchability.contract.runtime.executionMode}</div>
                      <div>Build-ready: {launchability.contract.foundry.buildReadyStatus ?? "-"}</div>
                      <div>Spec validator: {launchability.contract.foundry.specValidatorStatus ?? "-"}</div>
                      <div>Dry-run runtime: {launchability.contract.runtime.dryRunStatus}</div>
                      <div>Live runtime: {launchability.contract.runtime.liveStatus}</div>
                    </div>
                    {launchability.contract.blockers.length ? (
                      <div className="space-y-1">
                        <div className="font-semibold text-slate-800">Active blockers</div>
                        <div className="space-y-1">
                          {launchability.contract.blockers.map((blocker) => (
                            <div
                              key={blocker.id}
                              className={`rounded border px-2 py-1 ${
                                blocker.severity === "blocking"
                                  ? "border-red-200 bg-red-50 text-red-700"
                                  : "border-amber-200 bg-amber-50 text-amber-700"
                              }`}
                            >
                              <div className="font-semibold">
                                {blocker.source} · {blocker.severity}
                              </div>
                              <div>{blocker.detail}</div>
                              <div className="mt-1 text-[11px]">{blocker.resolution}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {launchability.contract.blockers.some((blocker) => blocker.source === "environment") ? (
                      <div className="rounded border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] text-blue-700">
                        Environment blockers are recalculated on submit with the values entered below.
                      </div>
                    ) : null}
                    {launchability.contract.nextActions.length ? (
                      <div className="space-y-1" data-testid="jarvis-v2-launchability-next-actions">
                        <div className="font-semibold text-slate-800">Next actions</div>
                        <div className="space-y-1">
                          {launchability.contract.nextActions.slice(0, 4).map((action) => (
                            <div key={action} className="rounded border border-slate-200 bg-slate-50 px-2 py-1">
                              {action}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    <a
                      className="font-medium text-blue-700 underline"
                      href={launchability.artifactUrls.launchabilityContract}
                    >
                      launchability_contract.v1.json
                    </a>
                  </>
                ) : launchabilityError ? (
                  <div className="text-red-700">{launchabilityError}</div>
                ) : (
                  <div className="text-slate-500">Launchability contract will appear once Foundry artifacts exist.</div>
                )}
              </div>

              <div className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                <div className="font-semibold text-slate-800">Expected environment keys</div>
                <div className="mt-1 break-words">{latestHandoff.input.environment.envKeys.join(", ") || "None"}</div>
              </div>

              {requiredEnvKeys.length ? (
                <div className="space-y-2 rounded border border-slate-200 bg-white p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold text-slate-800">
                      Environment contract ({isEnvContractReady ? "ready" : "incomplete"})
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => void exportEnvTemplate()}>
                      Copy .env template
                    </Button>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2">
                    {requiredEnvKeys.map((key) => {
                      const missing = !envValues[key]?.trim();
                      return (
                        <label key={key} className="space-y-1 text-xs">
                          <span className={missing ? "font-semibold text-amber-700" : "font-semibold text-slate-800"}>
                            {key}
                          </span>
                          <input
                            className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                            type="text"
                            value={envValues[key] ?? ""}
                            onChange={(event) => updateEnvValue(key, event.target.value)}
                            placeholder={missing ? "Required value" : "Configured"}
                          />
                        </label>
                      );
                    })}
                  </div>
                  {envExportMessage ? <div className="text-xs text-slate-600">{envExportMessage}</div> : null}
                  {!isEnvContractReady ? (
                    <div className="text-xs text-amber-700">Missing keys: {missingEnvKeys.join(", ")}</div>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-2 rounded border border-slate-200 bg-white p-2">
                <div className="text-xs font-semibold text-slate-800">High-risk stage approval</div>
                <label className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={highRiskStagesApproved}
                    onChange={(event) => setHighRiskStagesApproved(event.target.checked)}
                  />
                  Approval granted for high-risk stages
                </label>
                <div className="grid gap-2 md:grid-cols-2">
                  <label className="space-y-1 text-xs">
                    <span className="font-semibold text-slate-800">Approver</span>
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      type="text"
                      value={approvalApprover}
                      onChange={(event) => setApprovalApprover(event.target.value)}
                      placeholder="name or role"
                    />
                  </label>
                  <label className="space-y-1 text-xs">
                    <span className="font-semibold text-slate-800">Evidence reference</span>
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      type="text"
                      value={approvalEvidenceRef}
                      onChange={(event) => setApprovalEvidenceRef(event.target.value)}
                      placeholder="ticket id or URL"
                    />
                  </label>
                </div>
                <label className="space-y-1 text-xs">
                  <span className="font-semibold text-slate-800">Approval note</span>
                  <textarea
                    className="min-h-[56px] w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    value={approvalNote}
                    onChange={(event) => setApprovalNote(event.target.value)}
                    placeholder="Optional rationale"
                  />
                </label>
                <div className="text-[11px] text-slate-500">
                  If execution includes `high` risk stages, approver + evidence are required for deploy promotion.
                </div>
                {latestGovernanceApproval ? (
                  <div className="space-y-1 text-[11px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 font-semibold ${
                          latestGovernanceApproval.status === "approved"
                            ? "bg-emerald-100 text-emerald-700"
                            : latestGovernanceApproval.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {latestGovernanceApproval.status.toUpperCase()}
                      </span>
                      <span className={governanceEscalationBadgeClass(latestGovernanceApproval.escalation)}>
                        SLA {latestGovernanceApproval.escalation.toUpperCase()}
                      </span>
                      <span className="text-slate-600">risk {latestGovernanceApproval.riskTier}</span>
                    </div>
                    <div className="text-blue-700">
                      Deadline {new Date(latestGovernanceApproval.deadlineAt).toLocaleString()} |{" "}
                      {latestGovernanceApproval.overdueMs > 0
                        ? `overdue ${formatDuration(latestGovernanceApproval.overdueMs)}`
                        : `remaining ${formatDuration(latestGovernanceApproval.remainingMs)}`}
                    </div>
                    <div className="text-blue-700">
                      Latest Governance V2 decision:{" "}
                      {(latestGovernanceApproval.decision ?? latestGovernanceApproval.status).toUpperCase()} by{" "}
                      {latestGovernanceApproval.approver ?? "pending approver"} (
                      {latestGovernanceApproval.decidedAt
                        ? new Date(latestGovernanceApproval.decidedAt).toLocaleString()
                        : "not decided yet"}
                      ).
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-500">
                    No Governance V2 decision recorded yet for this handoff.
                  </div>
                )}
                <a className="text-[11px] font-medium text-blue-700 underline" href={withProjectQuery("/governance-v2")}>
                  Open Governance V2 approvals
                </a>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  data-testid="jarvis-v2-launch-button"
                  onClick={() => void launchJarvisV2Run()}
                  disabled={
                    isLaunchingRun ||
                    latestHandoff.status !== "READY_FOR_JARVIS" ||
                    isLaunchabilityHardBlocked ||
                    (provider === "openai" && !isOpenAiConfigured) ||
                    !isEnvContractReady
                  }
                >
                  {isLaunchingRun ? "Launching..." : "Launch Jarvis V2 run"}
                </Button>
                {latestHandoff.status !== "READY_FOR_JARVIS" ? (
                  <span className="text-xs text-amber-700">
                    Handoff status is `{latestHandoff.status}`. Resolve blockers in Foundry V2 first.
                  </span>
                ) : null}
                {isLaunchabilityHardBlocked ? (
                  <span className="text-xs text-red-700">
                    Launchability contract is blocked by non-environment blockers. Close them before launching Jarvis.
                  </span>
                ) : null}
              </div>

              {runError ? <div className="text-sm text-red-700">{runError}</div> : null}

              {deployCandidate ? (
                <div className="rounded border border-emerald-200 bg-emerald-50 p-2 text-xs text-emerald-900">
                  <div className="font-semibold">Deploy-ready candidate</div>
                  <div className="mt-1">
                    Run {deployCandidate.run.id} | {new Date(deployCandidate.run.startedAt).toLocaleString()} | provider: {deployCandidate.run.provider}
                  </div>
                  {deployCandidate.promotedByGovernance ? (
                    <div className="mt-1 text-[11px] text-emerald-800">
                      Promoted via Governance V2 decision by {deployCandidate.governanceApproval?.approver ?? "unknown approver"}.
                    </div>
                  ) : null}
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    {deployCandidate.run.archetype?.archiveUrl ? (
                      <a className="font-medium text-blue-700 underline" href={deployCandidate.run.archetype.archiveUrl} download>
                        Download release archive
                      </a>
                    ) : null}
                    <a className="font-medium text-blue-700 underline" href={deployCandidate.run.artifactUrls.deployHandoff}>
                      deploy_handoff.json
                    </a>
                    <a className="font-medium text-blue-700 underline" href={deployCandidate.run.artifactUrls.deliveryResult}>
                      delivery_result.json
                    </a>
                  </div>
                </div>
              ) : null}

              {runHistory.length ? (
                <div className="grid gap-3 lg:grid-cols-[320px_1fr]">
                  <div className="space-y-2 rounded border border-slate-200 bg-white p-2" data-testid="jarvis-v2-run-history-list">
                    <div className="text-xs font-semibold text-slate-800">Run history</div>
                    {runHistory.slice(0, 12).map((run) => (
                      <button
                        key={run.id}
                        type="button"
                        data-testid="jarvis-v2-run-history-item"
                        className={`w-full rounded border px-2 py-1 text-left text-xs ${
                          run.id === selectedRunId
                            ? "border-blue-300 bg-blue-50 text-blue-900"
                            : "border-slate-200 bg-white text-slate-700"
                        }`}
                        onClick={() => setSelectedRunId(run.id)}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{run.status.toUpperCase()}</span>
                          <span>{run.decision ?? "-"}</span>
                        </div>
                        <div className="mt-1 font-mono text-[11px]">{run.id}</div>
                        <div className="mt-1">
                          {run.provider} | {run.dryRun ? "dry-run" : "full-run"} | {new Date(run.startedAt).toLocaleString()}
                        </div>
                      </button>
                    ))}
                  </div>

                  {selectedRun ? (
                    <div className="space-y-3 rounded border border-slate-200 bg-white p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">Run details</div>
                          <div className="font-mono text-[11px] text-slate-600" data-testid="jarvis-v2-selected-run-id">{selectedRun.id}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={selectedRun.status === "succeeded" ? "text-xs font-semibold text-emerald-700" : selectedRun.status === "failed" ? "text-xs font-semibold text-red-700" : "text-xs font-semibold text-slate-700"}>
                            {selectedRun.status.toUpperCase()}
                          </span>
                          <span className={selectedRun.decision === "GO" ? "text-xs font-semibold text-emerald-700" : "text-xs font-semibold text-amber-700"}>
                            {selectedRun.decision ?? "-"}
                          </span>
                        </div>
                      </div>

                      <div className="grid gap-2 text-xs text-slate-700 md:grid-cols-2">
                        <div>Provider: {selectedRun.provider}</div>
                        <div>Mode: {selectedRun.dryRun ? "dry-run" : "full-run"}</div>
                        <div>Started: {new Date(selectedRun.startedAt).toLocaleString()}</div>
                        <div>Finished: {selectedRun.finishedAt ? new Date(selectedRun.finishedAt).toLocaleString() : "-"}</div>
                        <div>Archetype ID: {selectedRun.archetype?.archetypeId ?? "-"}</div>
                        <div>Environment: {selectedRun.environment}</div>
                        <div>Delivery mode: {selectedRun.assembly?.deliveryMode ?? "-"}</div>
                        <div>Domain family: {selectedRun.assembly?.domainFamily ?? "-"}</div>
                        <div>Execution stages: {selectedRun.executionGraph?.stageCount ?? 0}</div>
                        <div>
                          High-risk stages:{" "}
                          {selectedRun.executionGraph?.highRiskStageIds?.length
                            ? selectedRun.executionGraph.highRiskStageIds.join(", ")
                            : "-"}
                        </div>
                        <div>
                          Failed stages:{" "}
                          {selectedRun.executionGraph?.failedStageIds?.length
                            ? selectedRun.executionGraph.failedStageIds.join(", ")
                            : "-"}
                        </div>
                        <div>Approval required: {selectedRun.approval.required ? "yes" : "no"}</div>
                        <div>Approval provided: {selectedRun.approval.provided ? "yes" : "no"}</div>
                        <div>Approval status: {selectedRun.approval.approved ? "approved" : "not approved"}</div>
                        <div>Approver: {selectedRun.approval.approver ?? "-"}</div>
                        <div className="md:col-span-2 break-all">
                          Evidence reference: {selectedRun.approval.evidenceRef ?? "-"}
                        </div>
                        {selectedRun.approval.note ? (
                          <div className="md:col-span-2 break-all">Approval note: {selectedRun.approval.note}</div>
                        ) : null}
                        {selectedRun.archetype?.outputDir ? <div className="md:col-span-2 break-all">Output directory: {selectedRun.archetype.outputDir}</div> : null}
                        {selectedRun.assembly ? (
                          <div className="md:col-span-2 rounded border border-slate-200 bg-white p-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Assembly truth
                              </div>
                              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                selectedRun.assembly.status === "assembled"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-red-200 bg-red-50 text-red-700"
                              }`}>
                                {selectedRun.assembly.status}
                              </span>
                              <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                Build blockers {selectedRun.assembly.buildBlockers.blocking}/{selectedRun.assembly.buildBlockers.watch}
                              </span>
                            </div>
                            <div className="mt-3 grid gap-2 text-[11px] text-slate-700 md:grid-cols-2">
                              <div>Primary flows: {selectedRun.assembly.primaryFlowIds.join(", ") || "-"}</div>
                              <div>Security pack: {selectedRun.assembly.securityPack?.title ?? "-"}</div>
                              {selectedRun.assembly.securityPack ? (
                                <>
                                  <div>RBAC template: {selectedRun.assembly.securityPack.rbacTemplateId}</div>
                                  <div>Tenant isolation: {selectedRun.assembly.securityPack.tenantIsolationMode}</div>
                                  <div>Secret posture: {selectedRun.assembly.securityPack.secretHandlingMode}</div>
                                  <div>SSO posture: {selectedRun.assembly.securityPack.ssoProfile}</div>
                                </>
                              ) : null}
                              <div>QA pack: {selectedRun.assembly.qaPack?.title ?? "-"}</div>
                              <div>Evidence artifacts: {selectedRun.assembly.evidenceArtifacts.join(", ") || "-"}</div>
                              {selectedRun.assembly.securityPack?.evidenceBundleArtifacts?.length ? (
                                <div>Security evidence: {selectedRun.assembly.securityPack.evidenceBundleArtifacts.join(", ")}</div>
                              ) : null}
                            </div>
                            {selectedRun.assembly.customDeltaSummary ? (
                              <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                                {selectedRun.assembly.customDeltaSummary}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {selectedRun.brickSelection ? (
                          <div className="md:col-span-2 rounded border border-slate-200 bg-slate-50 p-3">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Frozen brick selection
                              </div>
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                Required {selectedRun.brickSelection.requiredBrickIds.length}
                              </span>
                              <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                Recommended {selectedRun.brickSelection.recommendedBrickIds.length}
                              </span>
                              <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                                Sensitive{" "}
                                {
                                  selectedRun.brickSelection.entries.filter((entry) => entry.sensitive).length
                                }
                              </span>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {selectedRun.brickSelection.entries
                                .filter((entry) => entry.selection !== "rejected")
                                .slice(0, 8)
                                .map((entry) => (
                                  <span
                                    key={`${entry.selection}-${entry.brickId}`}
                                    className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                                      entry.selection === "required"
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : entry.selection === "recommended"
                                          ? "border-blue-200 bg-blue-50 text-blue-700"
                                          : "border-slate-200 bg-white text-slate-700"
                                    }`}
                                    title={entry.rationale.join(" ")}
                                  >
                                    {entry.title}
                                  </span>
                                ))}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div
                        data-testid="jarvis-v2-launch-lane-status"
                        className="rounded border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Launch lane status
                            </div>
                            <div className="mt-1 text-sm font-semibold text-slate-900">
                              No hidden shell step: Studio posts to the backend control plane and this run remains visible here.
                            </div>
                          </div>
                          <span className={launchLanePhaseBadgeClass(selectedRun.launchLane.phase)}>
                            {selectedRun.launchLane.phase}
                          </span>
                        </div>
                        <div className="mt-3 grid gap-2 text-[11px] text-slate-700 md:grid-cols-2">
                          <div>Current stage ID: {selectedRun.launchLane.currentStageId ?? "-"}</div>
                          <div>Current stage label: {selectedRun.launchLane.currentStageLabel ?? "-"}</div>
                          <div>Updated: {new Date(selectedRun.launchLane.updatedAt).toLocaleString()}</div>
                          <div>History events: {selectedRun.launchLane.history.length}</div>
                        </div>
                        {selectedRun.launchLane.history.length ? (
                          <div className="mt-3 space-y-2">
                            {selectedRun.launchLane.history.map((entry, index) => (
                              <div
                                key={`${entry.phase}-${entry.at}-${index}`}
                                className="rounded border border-slate-200 bg-white px-2 py-2 text-[11px] text-slate-700"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <span className={launchLanePhaseBadgeClass(entry.phase)}>{entry.phase}</span>
                                  <span>{new Date(entry.at).toLocaleString()}</span>
                                </div>
                                <div className="mt-1">{entry.detail}</div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>

                      {selectedRun.archetype?.preview ? (
                        <div
                          data-testid="jarvis-v2-vercel-preview-status"
                          className="rounded border border-slate-200 bg-white p-3"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Vercel preview
                              </div>
                              <div className="mt-1 text-sm font-semibold text-slate-900">
                                Controlled deploy result from the generated runtime package.
                              </div>
                            </div>
                            <span
                              className={
                                selectedRun.archetype.preview.status === "deployed"
                                  ? "rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
                                  : selectedRun.archetype.preview.status === "failed"
                                    ? "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700"
                                    : selectedRun.archetype.preview.status === "blocked"
                                      ? "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                                      : "rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                              }
                            >
                              {selectedRun.archetype.preview.status}
                            </span>
                          </div>
                          <div className="mt-3 grid gap-2 text-[11px] text-slate-700 md:grid-cols-2">
                            <div>Deployment ID: {selectedRun.archetype.preview.deploymentId ?? "-"}</div>
                            <div>Ready state: {selectedRun.archetype.preview.readyState ?? "-"}</div>
                            <div>Preview URL: {selectedRun.archetype.preview.previewUrl ? <a className="font-medium text-blue-700 underline" href={selectedRun.archetype.preview.previewUrl} target="_blank" rel="noreferrer">open preview</a> : "-"}</div>
                            <div>Inspect URL: {selectedRun.archetype.preview.inspectUrl ? <a className="font-medium text-blue-700 underline" href={selectedRun.archetype.preview.inspectUrl} target="_blank" rel="noreferrer">open inspect</a> : "-"}</div>
                            <div>Generated at: {new Date(selectedRun.archetype.preview.generatedAt).toLocaleString()}</div>
                            <div>Inspect artifact: {selectedRun.archetype.preview.inspectPath ?? "-"}</div>
                          </div>
                          {selectedRun.archetype.preview.notes?.length ? (
                            <div className="mt-3 rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                              {selectedRun.archetype.preview.notes.join(" ")}
                            </div>
                          ) : null}
                          {selectedRun.archetype.preview.error ? (
                            <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                              {selectedRun.archetype.preview.error}
                            </div>
                          ) : null}
                          {selectedRun.archetype.preview.stdoutExcerpt?.length ? (
                            <div className="mt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Deploy log excerpt
                              </div>
                              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                                {selectedRun.archetype.preview.stdoutExcerpt.join("\n")}
                              </pre>
                            </div>
                          ) : null}
                          {selectedRun.archetype.preview.stderrExcerpt?.length ? (
                            <div className="mt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Error excerpt
                              </div>
                              <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700">
                                {selectedRun.archetype.preview.stderrExcerpt.join("\n")}
                              </pre>
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      <div className="flex flex-wrap items-center gap-2">
                        {selectedRun.archetype?.archiveUrl ? (
                          <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.archetype.archiveUrl} download>
                            Download app archive (.tgz)
                          </a>
                        ) : null}
                        <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.artifactUrls.runSummary}>
                          run_summary.json
                        </a>
                        <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.artifactUrls.executionSnapshot}>
                          execution_snapshot.json
                        </a>
                        <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.artifactUrls.deployHandoff}>
                          deploy_handoff.json
                        </a>
                        <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.artifactUrls.deliveryResult}>
                          delivery_result.json
                        </a>
                        <a className="text-xs font-medium text-blue-700 underline" href={selectedRun.artifactUrls.vercelPreview}>
                          vercel_preview.json
                        </a>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={refreshingRunId === selectedRun.id}
                          onClick={() => void refreshRunArtifacts(selectedRun.id)}
                        >
                          {refreshingRunId === selectedRun.id ? "Refreshing..." : "Refresh artifacts"}
                        </Button>
                      </div>

                      {selectedRun.error ? (
                        <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                          {selectedRun.error}
                        </div>
                      ) : null}

                      {selectedRun.assembly?.steps?.length ? (
                        <div className="space-y-2">
                          <div className="text-xs font-semibold text-slate-800">Assembly stages</div>
                          <div className="grid gap-2 md:grid-cols-2">
                            {selectedRun.assembly.steps.slice(0, 8).map((step) => (
                              <div key={step.id} className="rounded border border-slate-200 bg-white p-2">
                                <div className="flex items-center justify-between gap-2 text-[11px]">
                                  <div className="font-semibold text-slate-800">{step.title}</div>
                                  <div className={
                                    step.status === "passed"
                                      ? "font-semibold text-emerald-700"
                                      : step.status === "failed"
                                        ? "font-semibold text-red-700"
                                        : step.status === "skipped"
                                          ? "font-semibold text-amber-700"
                                          : "font-semibold text-slate-600"
                                  }>
                                    {step.status.toUpperCase()}
                                  </div>
                                </div>
                                <div className="mt-1 text-[11px] text-slate-500">
                                  {step.kind} · {step.riskTier} · attempts {step.attempts}/{Math.max(1, step.retryBudget + 1)}
                                </div>
                                {step.reason ? (
                                  <div className="mt-2 text-[11px] text-red-600">{step.reason}</div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <div className="text-xs font-semibold text-slate-800">Smoke checks</div>
                        {selectedRun.archetype?.smokeResults?.length ? (
                          selectedRun.archetype.smokeResults.map((result) => (
                            <div key={result.id} className="rounded border border-slate-200 bg-slate-50 p-2">
                              <div className="flex items-center justify-between gap-2 text-[11px]">
                                <div className="font-mono text-slate-700">{result.id}</div>
                                <div className={result.status === "passed" ? "font-semibold text-emerald-700" : "font-semibold text-red-700"}>
                                  {result.status.toUpperCase()}
                                </div>
                              </div>
                              {result.message ? (
                                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap text-[11px] text-slate-700">
                                  {result.message}
                                </pre>
                              ) : null}
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-slate-500">No smoke checks recorded.</div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-xs text-slate-600">No Jarvis V2 run yet for this handoff.</div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-amber-700">
            No Foundry V2 handoff exists yet for project `{projectId}`.
          </div>
        )}
      </Card>

      <div className="grid gap-3 md:grid-cols-3">
        {V2_MODULES.map((module) => (
          <Card
            key={module.id}
            className={activeModule === module.id ? "border-l-4 border-blue-500" : "border-l-4 border-slate-200"}
            bodyClassName="flex h-full flex-col gap-3"
          >
            <div className="space-y-1">
              <div className="text-base font-semibold text-slate-900">{module.title}</div>
              <p className="text-sm text-slate-600">{module.description}</p>
            </div>
            <div className="mt-auto">
              <Button
                variant={activeModule === module.id ? "primary" : "secondary"}
                size="sm"
                onClick={() => navigate(withProjectQuery(module.route))}
              >
                Open module
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {PIPELINE_STEPS.map((step) => (
          <Card key={step.title} className="border border-slate-200" bodyClassName="flex h-full flex-col gap-2">
            <div className="text-sm font-semibold text-slate-900">{step.title}</div>
            <p className="text-sm text-slate-600">{step.description}</p>
            <div className="mt-auto text-xs text-slate-500">Output: {step.expectedOutput}</div>
          </Card>
        ))}
      </div>
      </div>
    </FoundryV2AccessGuard>
  );
}
